import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { IS_DEMO } from "../env";
import {
  ATTACHMENTS,
  isOwnOriginUrl,
  storagePathOf,
  SUPABASE_ORIGIN,
  type StorageFile,
} from "./storageSrc";

/** Resolves to a signed URL. A null result or a thrown error both count as a failure. */
export type StorageSigner = (
  bucket: string,
  path: string,
  seconds: number,
) => Promise<string | null>;
export const SIGNED_URL_SECONDS = 3600;
/** Refresh 10 min before the token expires, so long-open pages keep working links. */
const REFRESH_MS = (SIGNED_URL_SECONDS - 10 * 60) * 1000;
const SIGN_FAILED = "storage_sign_failed";

/** Tests inject a signer; the app falls back to Supabase (none in the FakeRest demo). */
export const StorageSignerContext = createContext<StorageSigner | null>(null);
/** Origin whose storage URLs are ours (the configured Supabase URL); tests override it. */
export const StorageOriginContext = createContext<string | null>(
  SUPABASE_ORIGIN,
);

export const supabaseSigner: StorageSigner = async (bucket, path, seconds) => {
  const { data, error } = await getSupabaseClient()
    .storage.from(bucket)
    .createSignedUrl(path, seconds);
  if (error || !data?.signedUrl) throw new Error(SIGN_FAILED);
  return data.signedUrl;
};

// Failures throw, never resolve as null: react-query does not cache errors as fresh data, so a
// transient failure retries within seconds and again on the next mount, instead of blanking the
// file for 50 min.
const signedUrlQuery = (signer: StorageSigner | null, path: string | null) => ({
  queryKey: ["gavion-signed-url", ATTACHMENTS, path],
  queryFn: async () => {
    const url =
      signer && path
        ? await signer(ATTACHMENTS, path, SIGNED_URL_SECONDS)
        : null;
    if (!url) throw new Error(SIGN_FAILED);
    return url;
  },
  staleTime: REFRESH_MS,
  gcTime: 55 * 60 * 1000,
  retry: 2,
  retryDelay: (attempt: number) => Math.min(500 * 2 ** attempt, 4000),
  // Signed URLs are 1 h bearer capabilities: keep them out of the persisted mobile cache
  // (MobileAdmin's shouldDehydrateQuery skips `meta.persist === false`).
  meta: { persist: false },
});

function useStoragePath() {
  const injected = useContext(StorageSignerContext);
  const origin = useContext(StorageOriginContext);
  const signer = injected ?? (IS_DEMO ? null : supabaseSigner);
  const pathOf = useCallback(
    (file: StorageFile) =>
      signer ? storagePathOf(file, ATTACHMENTS, origin) : null,
    [signer, origin],
  );
  // Unsignable sources: data:/blob:/external URLs render as-is, but a URL on our own origin that
  // failed the path guard (traversal, encoded, other bucket) is never rendered.
  const unsigned = useCallback(
    (file: StorageFile) =>
      signer && isOwnOriginUrl(file?.src, origin)
        ? undefined
        : (file?.src ?? undefined),
    [signer, origin],
  );
  return { signer, pathOf, unsigned };
}

/**
 * Display URL for a stored file (spec 11 §6, batch 7: the attachments bucket is private). Our objects
 * get a 1 h signed URL, refreshed every 50 min; other sources are returned unchanged. undefined while
 * signing or while signing fails — callers show their fallback, never the stale public URL.
 */
export function useStorageSrc(file: StorageFile): string | undefined {
  const { signer, pathOf, unsigned } = useStoragePath();
  const path = pathOf(file);
  const q = useQuery({
    ...signedUrlQuery(signer, path),
    enabled: path !== null,
    refetchInterval: REFRESH_MS,
  });
  if (path === null) return unsigned(file);
  return q.data ?? undefined;
}

/** Imperative variant for event handlers (vCard export); shares the signed-URL cache. */
export function useResolveStorageSrc(): (
  file: StorageFile,
) => Promise<string | undefined> {
  const queryClient = useQueryClient();
  const { signer, pathOf, unsigned } = useStoragePath();
  return useCallback(
    async (file: StorageFile) => {
      const path = pathOf(file);
      if (path === null) return unsigned(file);
      try {
        return await queryClient.fetchQuery(signedUrlQuery(signer, path));
      } catch {
        return undefined;
      }
    },
    [queryClient, signer, pathOf, unsigned],
  );
}
